import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Lock, ShieldCheck, Phone, MapPin, Home, Users, GraduationCap, Briefcase, Heart, HeartHandshake, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ProfileView() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [shortlisted, setShortlisted] = useState(false);
  const [interest, setInterest] = useState(null);      // interest I SENT to them
  const [incoming, setIncoming] = useState(null);      // interest they SENT to me (Pending → Accept)
  const { user } = useAuth();

  const loadInterests = () => {
    api.get("/interests/sent").then((r) => {
      const it = r.data.items.find((x) => x.interest.to_user === id);
      setInterest(it ? it.interest : null);
    }).catch(() => {});
    api.get("/interests/received").then((r) => {
      const it = r.data.items.find((x) => x.interest.from_user === id);
      setIncoming(it ? it.interest : null);
    }).catch(() => {});
  };

  useEffect(() => {
    api.get(`/profiles/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/interests/shortlist").then((r) => setShortlisted(r.data.profiles.some((x) => x.id === id))).catch(() => {});
    loadInterests();
  }, [id]);

  const toggleShortlist = async () => {
    try { const r = await api.post(`/interests/shortlist/${id}`); setShortlisted(r.data.shortlisted); toast.success(r.data.shortlisted ? "Shortlisted" : "Removed from shortlist"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const express = async () => {
    try { const r = await api.post(`/interests/express/${id}`); setInterest({ id: r.data.id, status: r.data.status }); toast.success("Interest sent"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const acceptIncoming = async () => {
    if (!incoming) return;
    try {
      await api.patch(`/interests/${incoming.id}/status`, { status: "Accepted" });
      toast.success("Interest accepted — you can now chat");
      loadInterests();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const mutuallyAccepted = interest?.status === "Accepted" || incoming?.status === "Accepted";

  if (!p) return <div className="max-w-5xl mx-auto p-10 text-slate-600">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4">
          <Card className="overflow-hidden border-[#EAE3D2]">
            <div className="h-80 bg-[#F3EFE6] relative group" data-testid="profile-photo-carousel">
              {(() => {
                const photos = [p.profile_photo_path, ...(p.gallery_paths || [])].filter(Boolean);
                if (photos.length === 0) {
                  return (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#6B1D2F]/10 to-[#D4AF37]/10">
                      <span className="font-serif text-8xl text-[#6B1D2F]/50">{p.full_name?.[0]}</span>
                    </div>
                  );
                }
                const idx = Math.min(photoIdx, photos.length - 1);
                return (
                  <>
                    <img src={fileUrl(photos[idx])} alt={p.full_name} className="w-full h-full object-cover" />
                    {photos.length > 1 && (
                      <>
                        <button type="button" data-testid="carousel-prev" onClick={() => setPhotoIdx((idx - 1 + photos.length) % photos.length)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-[#6B1D2F] text-lg font-bold hover:bg-white">‹</button>
                        <button type="button" data-testid="carousel-next" onClick={() => setPhotoIdx((idx + 1) % photos.length)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-[#6B1D2F] text-lg font-bold hover:bg-white">›</button>
                        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                          {photos.map((_, i) => (
                            <button key={i} type="button" data-testid={`carousel-dot-${i}`} onClick={() => setPhotoIdx(i)}
                              className={`w-2 h-2 rounded-full ${i === idx ? "bg-white" : "bg-white/50"}`} />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="p-5">
              <h1 className="font-serif text-2xl text-slate-900">{p.full_name}</h1>
              <p className="text-sm text-slate-600">{p.age} years · {p.height_cm} cm · {p.marital_status}</p>
              {p.verification_status === "verified" ? (
                <Badge className="mt-3 bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="verified-badge"><ShieldCheck className="w-3 h-3 mr-1" /> Verified</Badge>
              ) : (
                <Badge className="mt-3 bg-slate-100 text-slate-700 border-slate-200">Not verified</Badge>
              )}
              <div className="mt-4 space-y-2">
                {mutuallyAccepted ? (
                  <Link to={`/chat/${id}`}><Button data-testid="open-chat-btn" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"><MessageCircle className="w-4 h-4 mr-2" /> Open chat</Button></Link>
                ) : incoming?.status === "Pending" ? (
                  <Button onClick={acceptIncoming} data-testid="accept-interest-btn" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
                    <HeartHandshake className="w-4 h-4 mr-2" /> Accept Interest
                  </Button>
                ) : interest ? (
                  <Button disabled data-testid="express-interest-btn" className="w-full bg-[#6B1D2F]/60 text-white rounded-full">
                    <HeartHandshake className="w-4 h-4 mr-2" /> Interest {interest.status}
                  </Button>
                ) : (
                  <Button onClick={express} data-testid="express-interest-btn" className="w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full">
                    <HeartHandshake className="w-4 h-4 mr-2" /> Express Interest
                  </Button>
                )}
                <Button onClick={toggleShortlist} variant="outline" data-testid="shortlist-btn" className={`w-full rounded-full ${shortlisted ? "bg-[#6B1D2F]/10 border-[#6B1D2F] text-[#6B1D2F]" : "border-[#6B1D2F]/30 text-[#6B1D2F]"}`}>
                  <Heart className={`w-4 h-4 mr-2 ${shortlisted ? "fill-[#6B1D2F]" : ""}`} /> {shortlisted ? "Shortlisted" : "Shortlist"}
                </Button>
              </div>
            </div>
          </Card>

          {p.video_intro_path && (
            <Card className="mt-4 p-4 border-[#EAE3D2] bg-white overflow-hidden">
              <p className="text-xs uppercase tracking-widest text-[#B8860B] font-semibold mb-2">Video intro</p>
              <video controls src={fileUrl(p.video_intro_path)} className="w-full rounded-lg bg-black" data-testid="profile-video-intro" />
            </Card>
          )}

          {p._masked?.visa_share_code && !isIndiaProfile(p) && (
            <UpgradeCard reason="Unlock mobile number & visa share code" />
          )}
          {!p.visa_status && user?.id !== p.id && !isIndiaProfile(p) && (
            <Card className="p-5 border-2 border-[#1E3A8A]/40 bg-blue-50/40" data-testid="immigration-upgrade-card">
              <p className="text-xs uppercase tracking-widest font-semibold text-[#1E3A8A]">£399 Verified Plan</p>
              <h3 className="font-serif text-xl text-slate-900 mt-1">Immigration details &amp; government verification</h3>
              <p className="text-sm text-slate-600 mt-1">See the Visa Verified badge and the full government check — on mutual interest — with the Verified plan.</p>
              <Link to="/pricing"><Button data-testid="profile-immigration-upgrade-btn" className="mt-3 w-full bg-[#1E3A8A] hover:bg-[#172c66] text-white rounded-full">View plans</Button></Link>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2 space-y-5">
          <Section icon={<GraduationCap className="w-5 h-5 text-[#6B1D2F]" />} title="Education & Profession">
            <Row k="Education" v={p.education} />
            <Row k="Profession" v={p.profession} />
            <Row k="Company" v={p.company} />
            <Row k="Annual income" v={formatIncome(p)} />
          </Section>

          <Section icon={<Users className="w-5 h-5 text-[#6B1D2F]" />} title="Community">
            <Row k="Religion" v={p.religion} />
            <Row k="Caste / Community" v={p.caste} />
            <Row k="Mother tongue" v={p.mother_tongue} />
          </Section>

          {!isIndiaProfile(p) && (
            <Section icon={<Briefcase className="w-5 h-5 text-[#6B1D2F]" />} title="Immigration">
              <Row k="Residence country" v={p.residence_country || "United Kingdom"} />
              <Row k="Visa type" v={p.visa_type} />
              <Row k="Visa expiry" v={p.visa_expiry_date} />
              {(p.residence_country || "United Kingdom") !== "Australia" && (
                <Row k="Visa share code" v={p.visa_share_code} masked={p._masked?.visa_share_code} />
              )}
              {p.visa_status && (
                <div className="flex flex-col py-1" data-testid="visa-status-block">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Visa verification status</span>
                  <VisaBadge status={p.visa_status} />
                </div>
              )}
            </Section>
          )}

          {p.visa_check && !isIndiaProfile(p) && (
            <Card className="p-6 border-2 border-emerald-200 bg-emerald-50/40" data-testid="visa-check-section">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-700" />
                  <h3 className="font-serif text-xl text-slate-900">Government Immigration Verification</h3>
                </div>
                {p.visa_check.mock && <Badge className="bg-slate-100 text-slate-600 border-slate-300">Demo check</Badge>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Row k="Check outcome" v={p.visa_check.outcome} />
                <Row k="Official reference" v={p.visa_check.reference || "—"} />
                {p.visa_check.match_type && <Row k="Match type" v={p.visa_check.match_type.replace(/_/g, " ")} />}
                {(p.visa_check.visa_route || p.visa_check.visa_class) && <Row k="Immigration route" v={(p.visa_check.visa_route || `${p.visa_check.visa_class}${p.visa_check.visa_subclass ? ` (subclass ${p.visa_check.visa_subclass})` : ""}`).replace(/_/g, " ")} />}
                {(p.visa_check.valid_from || p.visa_check.grant_date) && <Row k="Status valid from" v={p.visa_check.valid_from || p.visa_check.grant_date} />}
                <Row k="Status valid until" v={p.visa_check.expiry_date || "—"} />
                <Row k="Checked on" v={p.visa_check.checked_at ? new Date(p.visa_check.checked_at).toLocaleString() : "—"} />
                <Row k="Verified via" v={p.visa_check.provider === "didit" ? "Didit · Government registry" : "Government verification service"} />
              </div>
              {p.visa_check.entitlements && (
                <div className="mt-3 text-xs text-slate-600 leading-relaxed border-t border-emerald-200 pt-3" data-testid="visa-check-entitlements">
                  <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Entitlements</span>
                  {p.visa_check.entitlements}
                </div>
              )}
              {p.visa_check.conditions && (
                <div className="mt-3 text-xs text-slate-600 leading-relaxed border-t border-emerald-200 pt-3" data-testid="visa-check-conditions">
                  <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Official verification conditions</span>
                  {p.visa_check.conditions}
                </div>
              )}
              {p.visa_check.field_validations && Object.keys(p.visa_check.field_validations).length > 0 && (
                <div className="mt-3 border-t border-emerald-200 pt-3" data-testid="visa-check-fields">
                  <span className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Field-by-field verification</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(p.visa_check.field_validations).map(([k, v]) => (
                      <Badge key={k} className={String(v).includes("match") && !String(v).startsWith("no") ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-50 text-amber-800 border-amber-300"}>
                        {k.replace(/_/g, " ")}: {String(v).replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          <Section icon={<MapPin className="w-5 h-5 text-[#6B1D2F]" />} title="Current address">
            <Row k="City" v={p.current_city} />
            <Row k={isIndiaProfile(p) ? "PIN code" : "Postcode"} v={p.current_postcode} />
            <Row k="Address" v={p.current_address} masked={p._masked?.current_address} />
            <Row k="Mobile" v={p.mobile} masked={p._masked?.mobile} icon={<Phone className="w-3 h-3 inline" />} />
          </Section>

          {!isIndiaProfile(p) && (
            <Section icon={<Home className="w-5 h-5 text-[#6B1D2F]" />} title="Home country">
              <Row k="Country" v={p.home_country} />
              <Row k="City" v={p.home_country_city} />
              <Row k="Address" v={p.home_country_address} masked={p._masked?.home_country_address} />
              <Row k="Father" v={p.father_name} />
              <Row k="Father's occupation" v={p.father_occupation} />
              <Row k="Mother" v={p.mother_name} />
              <Row k="Mother's occupation" v={p.mother_occupation} />
              <Row k="Parents phone" v={p.parents_phone} masked={p._masked?.parents} />
            </Section>
          )}
          {isIndiaProfile(p) && (
            <Section icon={<Users className="w-5 h-5 text-[#6B1D2F]" />} title="Family">
              <Row k="Father" v={p.father_name} />
              <Row k="Father's occupation" v={p.father_occupation} />
              <Row k="Mother" v={p.mother_name} />
              <Row k="Mother's occupation" v={p.mother_occupation} />
              <Row k="Parents phone" v={p.parents_phone} masked={p._masked?.parents} />
            </Section>
          )}

          {p.about_me && (
            <Card className="p-6 border-[#EAE3D2]">
              <h3 className="font-serif text-xl text-slate-900 mb-2">About</h3>
              <p className="text-slate-700 leading-relaxed">{p.about_me}</p>
            </Card>
          )}
          {p.partner_preference && (
            <Card className="p-6 border-[#EAE3D2] bg-[#FAF6F0]">
              <h3 className="font-serif text-xl text-slate-900 mb-2">Partner preference</h3>
              <p className="text-slate-700 leading-relaxed">{p.partner_preference}</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function isIndiaProfile(p) {
  return p?.member_type === "india" || p?.residence_country === "India";
}

function formatIncome(p) {
  const val = Number(p?.annual_income_gbp || 0);
  if (!val) return "—";
  const rc = p?.residence_country;
  const mt = p?.member_type;
  let symbol = "£";
  if (mt === "india" || rc === "India") symbol = "₹";
  else if (rc === "Australia") symbol = "A$";
  return `${symbol}${val.toLocaleString()}`;
}

function VisaBadge({ status }) {
  const map = {
    verified: { label: "Visa Verified", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    pending: { label: "Verification Pending", cls: "bg-amber-50 text-amber-800 border-amber-300" },
    expired: { label: "Visa Expired", cls: "bg-red-50 text-red-800 border-red-300" },
    unverified: { label: "Not Verified", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  };
  const m = map[status] || map.unverified;
  return <Badge className={`${m.cls} w-fit`}>{m.label}</Badge>;
}

function Section({ icon, title, children }) {
  return (
    <Card className="p-6 border-[#EAE3D2] bg-white">
      <div className="flex items-center gap-2 mb-4">{icon}<h3 className="font-serif text-xl text-slate-900">{title}</h3></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>
    </Card>
  );
}

function Row({ k, v, masked, icon }) {
  return (
    <div className="flex flex-col py-1">
      <span className="text-xs uppercase tracking-wider text-slate-500">{k}</span>
      {masked ? (
        <span className="flex items-center gap-2">
          <span className="masked-field">████████████</span>
          <Lock className="w-3 h-3 text-amber-700" />
        </span>
      ) : (
        <span className="text-slate-800 font-medium">{icon}{v || "—"}</span>
      )}
    </div>
  );
}

function UpgradeCard({ reason }) {
  return (
    <Card className="p-5 border-2 border-[#D4AF37] plan-card-featured">
      <p className="text-xs uppercase tracking-widest font-semibold text-[#B8860B]">Premium</p>
      <h3 className="font-serif text-xl text-slate-900 mt-1">{reason}</h3>
      <p className="text-sm text-slate-600 mt-1">Upgrade to £199 Connect to see contact details for 6 months. £399 Verified adds government immigration verification details on mutual interest.</p>
      <Link to="/pricing"><Button data-testid="profile-upgrade-btn" className="mt-3 w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full">View plans</Button></Link>
    </Card>
  );
}
